import { Check, Mail, MessageSquareReply, X } from "lucide-react";

import { Accent, Monogram, SplitFeature, panel } from "@/features/platform/components/landing-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";
import { getLocale } from "@/paraglide/runtime.js";

const channel = "flex items-center gap-2 text-xs font-semibold tracking-[0.1em] text-muted-foreground uppercase";

/** The trainer's sample day, as the morning email lists it. */
const agenda = () => [
  { id: "ola", time: "07:00", what: m.platform_preview_ola() },
  { id: "ania", time: "12:15", what: m.platform_preview_ania() },
  {
    id: "trx",
    time: "18:00",
    what: `${m.platform_hero_chip_group()} · ${m.platform_widget_seats({ taken: 6, total: 8 })}`,
  },
];

/** How clients answered the "will you be there?" reminder. Sample people. */
const replies = () => [
  { id: "ola", name: "Ola Kowalska", coming: true },
  { id: "kuba", name: "Kuba Mazur", coming: false },
  { id: "ania", name: "Ania Wiśniewska", coming: true },
];

/**
 * What lands in the trainer's inbox, told beside it: the whole day each morning, and the clients'
 * answers to the reminder sent the day before their session.
 */
export const DayDigest = () => {
  const today = new Intl.DateTimeFormat(getLocale(), { weekday: "long" }).format(new Date());

  return (
    <SplitFeature
      eyebrow={m.platform_digest_eyebrow()}
      title={
        <>
          {m.platform_digest_title_lead()}
          <br />
          <Accent>{m.platform_digest_title_tail()}</Accent>
        </>
      }
      body={m.platform_digest_body()}
      className="grid gap-3.5 sm:grid-cols-[1.15fr_1fr]"
    >
      <div aria-hidden className={cn(panel, "flex flex-col p-5")}>
        <span className={channel}>
          <Mail className="size-4 text-[var(--site-ink)]" />
          {m.platform_digest_email_when()}
        </span>
        <b className="mt-4 text-xl font-semibold tracking-[-0.02em]">{m.platform_digest_subject({ day: today })}</b>
        <ul className="mt-4 grid gap-2 text-sm">
          {agenda().map((visit) => (
            <li key={visit.id} className="grid grid-cols-[48px_1fr] gap-2 rounded-xl bg-[var(--site-well)] px-3 py-2.5">
              <b className="font-semibold tabular-nums">{visit.time}</b>
              <span className="truncate">{visit.what}</span>
            </li>
          ))}
        </ul>
      </div>
      <div aria-hidden className={cn(panel, "flex flex-col p-5")}>
        <span className={channel}>
          <MessageSquareReply className="size-4 text-[var(--site-ink)]" />
          {m.platform_digest_replies()}
        </span>
        <ul className="mt-4 grid gap-2 text-sm">
          {replies().map((reply) => (
            <li key={reply.id} className="flex items-center gap-2.5 rounded-xl bg-[var(--site-well)] p-2">
              <Monogram name={reply.name} className="size-8 rounded-lg text-[11px]" />
              <span className="min-w-0 flex-1 truncate font-medium">{reply.name}</span>
              <span
                className={cn(
                  "inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold",
                  reply.coming ? "bg-primary text-primary-foreground" : "bg-destructive/15 text-destructive",
                )}
              >
                {reply.coming ? <Check className="size-3" strokeWidth={3} /> : <X className="size-3" strokeWidth={3} />}
                {reply.coming ? m.platform_digest_yes() : m.platform_digest_no()}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </SplitFeature>
  );
};
